import React, { useEffect, useState } from 'react';
import Modal from './ModalConversion';
import { urlTasks, endpointGetStatusComparisonReport } from '../services/Endpoints';
import { Loading } from './Loading';
import { theme } from '../theme';
import * as XLSX from 'xlsx';

export default function ComparisonReportModal({ show, setShow, channel = '', channels = [] }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filter, setFilter] = useState('');

    useEffect(() => {
        if (show) {
            getData();
        }
        return () => {
            setData([]);
            setFilter('');
        }
    }, [show, channel]);

    const getData = async () => {
        try {
            setLoading(true);
            const channelParam = channel ? `?channel=${encodeURIComponent(channel)}` : '';
            const response = await fetch(`${urlTasks}${endpointGetStatusComparisonReport}${channelParam}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                }
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setData(res.data || []);
            } else {
                setData([]);
                alert('No se pudieron obtener los datos: ' + (res.message || 'Error desconocido'));
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            alert('Error al obtener reporte: ' + error.message);
        }
    }

    const handleClose = () => setShow(false);

    // Nombre legible del canal activo
    const channelLabel = channel
        ? (channels.find(c => c.alias === channel)?.name || channel)
        : null;

    const handleExportExcel = () => {
        // Map filtered data to Excel format
        const excelData = filteredData.map(row => ({
            'Canal': row.channel_name,
            'Alias': row.channel_alias,
            'Títulos Seleccionados': row.inkit_selected,
            'Inkit ON': row.inkit_published,
            'Inkit OFF': row.inkit_inactive,
            'Store Total': row.store_total || 0,
            'Store ON': row.store_published,
            'Store OFF': row.store_inactive,
            'Fecha Reporte': row.report_date ? new Date(row.report_date).toLocaleString() : '',
            'Estado Sync': row.sync_status === 'completed' ? 'Completado' : row.sync_status === 'partial' ? 'Parcial' : 'Pendiente',
            'Última Sync': row.last_sync_date ? new Date(row.last_sync_date).toLocaleString() : '',
            'Diferencia ON': row.difference_on || 0,
            'Diferencia OFF': row.difference_off || 0,
            'Anterior Total': row.store_prev_total || 0,
            'Anterior ON': row.store_prev_published || 0,
            'Anterior OFF': row.store_prev_inactive || 0,
            'Fecha Anterior': row.prev_report_date ? new Date(row.prev_report_date).toLocaleString() : '',
            'Estado Sync Anterior': row.prev_sync_status === 'completed' ? 'Completado' : row.prev_sync_status === 'partial' ? 'Parcial' : 'Pendiente',
            'Última Sync Anterior': row.prev_last_sync_date ? new Date(row.prev_last_sync_date).toLocaleString() : '',
            'Dif. Reportes ON': row.store_diff_reports_on || 0,
            'Dif. Reportes OFF': row.store_diff_reports_off || 0,
            'Altas Enviadas': row.notifications?.altas_sent || 0,
            'Altas Pendientes': row.notifications?.altas_pending || 0,
            'Bajas Enviadas': row.notifications?.bajas_sent || 0,
            'Bajas Pendientes': row.notifications?.bajas_pending || 0
        }));

        // Create workbook and worksheet
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Reporte Comparativo');

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 10);
        const filename = `reporte_comparativo_${timestamp}.xlsx`;

        // Download file
        XLSX.writeFile(wb, filename);
    };

    const filteredData = data.filter(item =>
        item.channel_name.toLowerCase().includes(filter.toLowerCase()) ||
        item.channel_alias.toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <Modal isOpen={show} onClose={handleClose} fullscreen>
            <div className="p-3">
                <div className="d-flex align-items-baseline gap-3 mb-4" style={{ flexWrap: 'wrap' }}>
                    <h3 className="mb-0 text-white">Reporte General: Publicados Inkit vs Tienda</h3>
                    {channelLabel ? (
                        <span style={{
                            background: `rgba(\${theme.primaryRgb}, 0.15)`,
                            color: theme.primaryHover,
                            border: `1px solid rgba(${theme.primaryRgb}, 0.4)`,
                            borderRadius: 6,
                            padding: '2px 12px',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                        }}>
                            {channelLabel}
                        </span>
                    ) : (
                        <span style={{
                            background: 'rgba(var(--ink-overlay-rgb),0.07)',
                            color: 'rgba(var(--ink-overlay-rgb),0.45)',
                            border: '1px solid rgba(var(--ink-overlay-rgb),0.12)',
                            borderRadius: 6,
                            padding: '2px 12px',
                            fontSize: '0.85rem',
                        }}>
                            Todas las tiendas
                        </span>
                    )}
                </div>

                <div className="mb-3">
                    <input
                        type="text"
                        className="form-control bg-dark text-white border-secondary py-2"
                        placeholder="Filtrar por canal..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>

                {loading ? (
                    <Loading />
                ) : (
                    <div className="table-responsive">
                        <table className="table table-striped table-hover table-dark w-100">
                            <thead>
                                <tr>
                                    <th>Canal</th>
                                    <th className="text-center">Títulos Seleccionados Inkit<br />(ON/DISABLED/OFF)</th>
                                    <th className="text-center">Publicados Tienda<br />(ON/DISABLED)</th>
                                    <th className="text-center">Diferencia<br />(ON)</th>
                                    <th className="text-center">Anterior<br />(ON/DISABLED)</th>
                                    <th className="text-center">Dif. Reportes<br />(ON)</th>
                                    <th className="text-center">Últimas acciones (Enviados/Pendientes)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center">No hay datos disponibles</td>
                                    </tr>
                                ) : (
                                    filteredData.map((row, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <strong>{row.channel_name}</strong>
                                                <div className="text-muted small">{row.channel_alias}</div>
                                            </td>
                                            <td className="text-center">
                                                <div>
                                                    {/* <strong className="text-white me-2">{row.inkit_selected}</strong> */}
                                                    {/* <span className="text-muted mx-1">|</span> */}
                                                    <span className="text-success fw-bold">{row.inkit_published}</span>
                                                    <span className="text-muted mx-1">/</span>
                                                    <span className="text-danger">{row.inkit_disabled || 0}</span>
                                                    <span className="text-muted mx-1">/</span>
                                                    <span className="text-warning">{row.inkit_inactive || 0}</span>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                {row.has_report ? (
                                                    <>
                                                        <div>
                                                            {/* <strong className="text-white me-2">{row.store_total || 0}</strong> */}
                                                            {/* <span className="text-muted mx-1">|</span> */}
                                                            <span className="text-success fw-bold">{row.store_published}</span>
                                                            <span className="text-muted mx-1">/</span>
                                                            <span className="text-danger">{row.store_inactive || 0}</span>
                                                        </div>
                                                        {row.report_date && (
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--ink-text-dim)', marginTop: '4px' }}>
                                                                {new Date(row.report_date).toLocaleString()}
                                                            </div>
                                                        )}
                                                        {row.sync_status && (
                                                            <div style={{ marginTop: '4px' }}>
                                                                <span className={`badge ${row.sync_status === 'completed' ? 'bg-success' :
                                                                    row.sync_status === 'partial' ? 'bg-warning text-dark' :
                                                                        'bg-secondary'
                                                                    }`} style={{ fontSize: '0.65rem' }}>
                                                                    {row.sync_status === 'completed' ? 'Completado' :
                                                                        row.sync_status === 'partial' ? 'Parcial' :
                                                                            'Pendiente'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="badge bg-secondary">Sin Reporte</span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {row.has_report ? (
                                                    <div>
                                                        <span className={`badge ${row.difference_on === 0 ? 'bg-success' : 'bg-warning text-dark'} me-1`}>
                                                            ON: {row.difference_on > 0 ? `+${row.difference_on}` : row.difference_on}
                                                        </span>
                                                        {/* <span className={`badge ${row.difference_off === 0 ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                            OFF: {row.difference_off > 0 ? `+${row.difference_off}` : row.difference_off}
                                                        </span> */}
                                                    </div>
                                                ) : (
                                                    <span>-</span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {row.has_prev_report ? (
                                                    <>
                                                        <div>
                                                            {/* <strong className="text-white me-2">{row.store_prev_total || 0}</strong> */}
                                                            {/* <span className="text-muted mx-1">|</span> */}
                                                            <span className="text-success fw-bold">{row.store_prev_published}</span>
                                                            <span className="text-muted mx-1">/</span>
                                                            <span className="text-danger">{row.store_prev_inactive || 0}</span>
                                                        </div>
                                                        {row.prev_report_date && (
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--ink-text-dim)', marginTop: '4px' }}>
                                                                {new Date(row.prev_report_date).toLocaleString()}
                                                            </div>
                                                        )}
                                                        {row.prev_sync_status && (
                                                            <div style={{ marginTop: '4px' }}>
                                                                <span className={`badge ${row.prev_sync_status === 'completed' ? 'bg-success' :
                                                                    row.prev_sync_status === 'partial' ? 'bg-warning text-dark' :
                                                                        'bg-secondary'
                                                                    }`} style={{ fontSize: '0.65rem' }}>
                                                                    {row.prev_sync_status === 'completed' ? 'Completado' :
                                                                        row.prev_sync_status === 'partial' ? 'Parcial' :
                                                                            'Pendiente'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="badge bg-secondary">Sin Reporte</span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {row.has_prev_report ? (
                                                    <div>
                                                        <span className={`badge ${row.store_diff_reports_on === 0 ? 'bg-secondary' : (row.store_diff_reports_on > 0 ? 'bg-success' : 'bg-danger')} me-1`}>
                                                            ON: {row.store_diff_reports_on > 0 ? `+${row.store_diff_reports_on}` : row.store_diff_reports_on}
                                                        </span>
                                                        {/* <span className={`badge ${row.store_diff_reports_off === 0 ? 'bg-secondary' : (row.store_diff_reports_off > 0 ? 'bg-success' : 'bg-danger')}`}>
                                                            OFF: {row.store_diff_reports_off > 0 ? `+${row.store_diff_reports_off}` : row.store_diff_reports_off}
                                                        </span> */}
                                                    </div>
                                                ) : (
                                                    <span>-</span>
                                                )}
                                            </td>

                                            <td className="text-center">
                                                {row.notifications ? (
                                                    <div className="d-flex flex-column gap-1">
                                                        <div className="small">
                                                            <span className="fw-bold me-1">Altas:</span>
                                                            <span className="text-success">{row.notifications.altas_sent || 0}</span>
                                                            <span className="mx-1">/</span>
                                                            <span className="text-warning">{row.notifications.altas_pending || 0}</span>
                                                        </div>
                                                        <div className="small">
                                                            <span className="fw-bold me-1">Bajas:</span>
                                                            <span className="text-success">{row.notifications.bajas_sent || 0}</span>
                                                            <span className="mx-1">/</span>
                                                            <span className="text-warning">{row.notifications.bajas_pending || 0}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted small">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top border-secondary">
                    <button type="button" className="btn btn-success px-4" onClick={handleExportExcel}>
                        Descargar Excel
                    </button>
                    <button type="button" className="btn btn-outline-secondary px-4" onClick={handleClose}>
                        Cerrar
                    </button>
                </div>
            </div>
        </Modal>
    );
}

